import { Checkbox } from '@/components/ui/checkbox';
import { ButtonCheckbox } from '@/components/ui/button-checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/tailwind-utils';
import { FieldPath, FieldPathValue, FieldValues, UseFormReturn } from 'react-hook-form';

type CheckboxComponent = typeof Checkbox | typeof ButtonCheckbox;

type CheckboxGroupPropsWithTestId = (ComponentProps<typeof Checkbox> | ComponentProps<typeof ButtonCheckbox>) & {
  'data-testid'?: string;
};

interface FormCheckboxGroupProps<
  SingleOption,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  options: NonNullable<FieldPathValue<TFieldValues, TName>> extends SingleOption[]
    ? NonNullable<FieldPathValue<TFieldValues, TName>>
    : never;
  checkboxProps?: CheckboxGroupPropsWithTestId;
  optionLabel?: (option: SingleOption) => ReactNode;
  description?: ReactNode;
  horizontal?: boolean;
  CheckboxComponent?: CheckboxComponent;
  secondaryLabels?: (option: SingleOption, checked: boolean) => string | undefined;
  labelClassName?: string;
  outerClassName?: string;
}

export function FormCheckboxGroup<
  SingleOption,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  checkboxProps = {},
  options,
  optionLabel = (option) => String(option),
  labelClassName = '',
  outerClassName = '',
  description,
  horizontal = false,
  CheckboxComponent = Checkbox,
  secondaryLabels,
}: FormCheckboxGroupProps<SingleOption, TFieldValues, TName>) {
  const { required, 'data-testid': dataTestId, ...others } = checkboxProps || {};
  if (!Array.isArray(options)) {
    throw new Error('options for FormCheckboxGroup must be an array');
  }

  const castedOptions = options as SingleOption[];

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem className="w-full">
          <div className="mb-4">
            <FormLabel className="font-bold">
              {label}
              {required && '*'}
            </FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
          </div>
          <div className={cn('flex gap-3', horizontal ? 'flex-row flex-wrap' : 'flex-col', outerClassName)}>
            {castedOptions.map((item) => (
              <FormField
                key={String(item)}
                control={form.control}
                name={name}
                render={({ field }) => {
                  const itemString = String(item);
                  const isChecked = field.value?.includes(item);

                  // Get secondary label if the function is provided
                  const secondaryLabel = secondaryLabels ? secondaryLabels(item, isChecked) : undefined;

                  return (
                    <FormItem
                      key={itemString}
                      className={cn(
                        'flex items-center space-y-0',
                        CheckboxComponent === Checkbox ? 'flex-row space-x-3' : 'flex-col',
                      )}
                    >
                      <FormControl>
                        {CheckboxComponent === ButtonCheckbox ? (
                          <ButtonCheckbox
                            checked={field.value?.includes(item)}
                            onCheckedChange={(checked) => {
                              return checked
                                ? field.onChange([...field.value, item])
                                : field.onChange((field.value as SingleOption[])?.filter((value) => value !== item));
                            }}
                            secondaryLabel={secondaryLabel}
                            data-testid={dataTestId ? `${dataTestId}-${String(item)}` : undefined}
                            {...others}
                          >
                            {optionLabel(item)}
                          </ButtonCheckbox>
                        ) : (
                          <Checkbox
                            checked={field.value?.includes(item)}
                            onCheckedChange={(checked) => {
                              return checked
                                ? field.onChange([...field.value, item])
                                : field.onChange((field.value as SingleOption[])?.filter((value) => value !== item));
                            }}
                            data-testid={dataTestId ? `${dataTestId}-${String(item)}` : undefined}
                            {...others}
                          />
                        )}
                      </FormControl>
                      {CheckboxComponent === Checkbox && (
                        <FormLabel className={cn('font-normal', { 'text-gray-400': others.disabled }, labelClassName)}>
                          {optionLabel(item)}
                        </FormLabel>
                      )}
                    </FormItem>
                  );
                }}
              />
            ))}
          </div>
        </FormItem>
      )}
    />
  );
}
